import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ArrowUpRight, Copy, Clock } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Drafts() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [designs, setDesigns] = useState([]);
  const [search, setSearch] = useState("");

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return t("common.justNow");
    if (diff < 3600) return t("common.minutesAgo", { count: Math.floor(diff / 60) });
    if (diff < 86400) return t("common.hoursAgo", { count: Math.floor(diff / 3600) });
    return t("common.daysAgo", { count: Math.floor(diff / 86400) });
  };

  const load = async (q = "") => {
    const { data } = await api.get("/designs", { params: { search: q, limit: 200 } });
    setDesigns(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const tm = setTimeout(() => load(search), 300); return () => clearTimeout(tm); }, [search]);

  const handleDuplicate = async (id) => {
    try {
      const { data } = await api.post(`/designs/${id}/duplicate`, {});
      toast.success(t("home.duplicatedAs", { n: data.designNumber }));
      navigate(`/designer/${data.id}`);
    } catch (e) { toast.error(t("home.duplicateFailed")); }
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-10" data-testid="drafts-page">
      <h1 className="font-display text-2xl font-700 text-slate-900">{t("drafts.title")}</h1>
      <div className="mt-5 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder={t("drafts.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="draft-search-input" />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {designs.map((d) => (
          <div key={d.id} className="bhf-card overflow-hidden" data-testid={`draft-card-${d.id}`}>
            <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100">
              {d.previewImage?.url ? <img src={fileUrl(d.previewImage)} alt={d.designNumber} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-slate-400">{t("common.noPreview")}</div>}
            </div>
            <div className="p-3.5">
              <div className="flex items-center justify-between">
                <span className="font-mono-bhf text-[11px] font-medium text-indigo-600">{d.designNumber}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${d.status === "Final" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{d.status} · V{d.version}</span>
              </div>
              <div className="mt-1 truncate text-sm font-semibold text-slate-800">{d.productName || t("common.untitled")}</div>
              <div className="mt-0.5 truncate text-xs text-slate-500">{d.customer || t("common.noCustomer")} · {d.brandName || "—"}</div>
              <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" /> {timeAgo(d.updatedAt)}</div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => navigate(`/designer/${d.id}`)} data-testid={`open-draft-${d.id}`}><ArrowUpRight className="h-3.5 w-3.5" /> {t("common.open")}</Button>
                <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => handleDuplicate(d.id)} data-testid={`duplicate-draft-${d.id}`}><Copy className="h-3.5 w-3.5" /> {t("common.duplicate")}</Button>
              </div>
            </div>
          </div>
        ))}
        {designs.length === 0 && <div className="col-span-full rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">{t("drafts.none")}</div>}
      </div>
    </div>
  );
}
